"use client";

import { createElement, type CSSProperties, type ReactNode } from "react";
import { useInView } from "./Reveal";

type HeadingTag = "h1" | "h2" | "h3" | "h4" | "p" | "div" | "span";

interface RevealLinesProps {
  /**
   * Pre-split line content, one entry per visual line. Entries may be
   * rich nodes (e.g. an <em> inside a line). Author the split yourself
   * per breakpoint-safe clause; this is a server-decided split, not a
   * runtime measurement.
   */
  lines: ReactNode[];
  /** Rendered element, defaults to h2. One h1 per page. */
  as?: HeadingTag;
  className?: string;
  /** Delay before the first line, ms. */
  delay?: number;
  /** Per-line stagger, ms. Brief default 90. */
  stagger?: number;
  threshold?: number;
  once?: boolean;
  style?: CSSProperties;
  id?: string;
}

/**
 * SI-01 line-mask heading rise. Each line sits inside an
 * overflow-hidden mask and rises from translateY(115%) to 0 with a
 * staggered delay. Headings only; never body copy. CSS lives in
 * globals.css under .silk-lines, gated behind `.js` and collapsed to
 * static under prefers-reduced-motion.
 */
export function RevealLines({
  lines,
  as = "h2",
  className = "",
  delay = 0,
  stagger = 90,
  threshold = 0.15,
  once = true,
  style,
  id,
}: RevealLinesProps) {
  const [ref, visible] = useInView<HTMLElement>({
    threshold,
    rootMargin: "0px 0px -10% 0px",
    once,
  });

  return createElement(
    as,
    {
      ref,
      id,
      className: `silk-lines ${className}`,
      style,
      "data-reveal-in": visible ? "true" : "false",
    },
    lines.map((line, i) => (
      <span key={i} className="silk-line">
        <span
          className="silk-line-inner"
          style={{ "--line-delay": `${delay + i * stagger}ms` } as CSSProperties}
        >
          {line}
        </span>
      </span>
    ))
  );
}
